import React from "react";
import { useTranslation } from "react-i18next";

export interface CommodityType {
  id: number;
  title: string;
}

export interface Company {
  id: number;
  name: string;
  trading_as: string;
  commodity_types: CommodityType[];
  administrative_first_name: string;
  administrative_last_name: string;
  administrative_email: string;
  administrative_contact_number: string;
  designation: string;
  facility_required?: boolean;
  facility_amount?: number;
  debtor_type?: {
    debtor_type: string;
    id: number;
  };
  payment_terms?: {
    payment_term: string;
    id: number;
  };
  client_type?: number;
  profession?: number | string;
  reason_to_access?: string;
  credit_facility?: string | null;
}

export interface UserDetails {
  user_type: {
    id: number;
  };
  companies: Company[];
}

interface RenderOtherDetailsProps {
  userDetails: UserDetails;
}

const RenderOtherDetails: React.FC<RenderOtherDetailsProps> = ({
  userDetails,
}) => {
  const { t } = useTranslation();
  const userType = userDetails.user_type.id;

  switch (userType) {
    case 4: // Tier 4
      return (
        <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
              {t("myAccount.otherDetails.facilityRequired")}
            </span>
            <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
              {userDetails.companies?.[0]?.facility_required ? "Yes" : "No"}
            </h4>
          </div>
          {userDetails.companies?.[0]?.facility_required && (
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                {t("myAccount.otherDetails.facilityAmount")}
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                R{" "}
                {userDetails.companies?.[0]?.facility_amount?.toLocaleString() ||
                  "0"}
              </h4>
            </div>
          )}
        </div>
      );

    case 5: // Tier 5
      return (
        <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
              {t("myAccount.otherDetails.debtorType")}
            </span>
            <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
              {userDetails.companies?.[0]?.debtor_type?.debtor_type || "-"}
            </h4>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
              {t("myAccount.otherDetails.paymentTerms")}
            </span>
            <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
              {userDetails.companies?.[0]?.payment_terms?.payment_term || "-"}
            </h4>
          </div>
          <div className="tw-flex tw-gap-2 tw-items-start">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                {t("myAccount.otherDetails.clientType")}
              </span>
              <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                {userDetails.companies?.[0]?.client_type === 1
                  ? "ACC (Account)"
                  : "Cash"}
              </h4>
            </div>
            {userDetails.companies?.[0]?.client_type === 1 && (
              <div className="tw-bg-[#F8FAFC] tw-rounded-md tw-p-3 tw-border tw-border-[#E9EAEB]">
                <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
                  Credit Facility Number
                </span>
                <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
                  {userDetails.companies?.[0]?.credit_facility || "-"}
                </h4>
              </div>
            )}
          </div>
        </div>
      );

    case 6: // Tier 6
      return (
        <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
              {t("myAccount.otherDetails.profession")}
            </span>
            <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
              {userDetails.companies?.[0]?.profession || "-"}
            </h4>
          </div>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
              {t("myAccount.otherDetails.reasonToAccess")}
            </span>
            <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
              {userDetails.companies?.[0]?.reason_to_access || "-"}
            </h4>
          </div>
        </div>
      );

    case 7: // Tier 7
      return (
        <div className="tw-grid md:tw-grid-cols-3 sm:tw-grid-cols-12 tw-grid-cols-12 tw-gap-8">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-[#809199]">
              {t("myAccount.otherDetails.commodityTypes")}
            </span>
            <h4 className="tw-text-base tw-text-[#232323] tw-font-semibold">
              {userDetails.companies?.[0]?.commodity_types
                ?.map((commodity: CommodityType) => commodity.title)
                .join(", ") || "-"}
            </h4>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default RenderOtherDetails;
